import * as Yup from 'yup';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import LoadingButton from '@mui/lab/LoadingButton';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useRouter, useSearchParams } from 'src/routes/hooks';
import { paths } from 'src/routes/paths';
import { RouterLink } from 'src/routes/components';
import { EmailInboxIcon } from 'src/assets/icons';
import Iconify from 'src/components/iconify';
import FormProvider, { RHFCode, RHFTextField } from 'src/components/hook-form';
import { toast } from 'react-toastify';
import {
  useValidatePinMutation,
  useResendOtpMutation,
} from '../../../store/Reducer/auth';

export default function JWTValidatePinView() {
  const router = useRouter();
  const [validatePin] = useValidatePinMutation();
  const [resendOtp] = useResendOtpMutation();
  const searchParams = useSearchParams();
  const email = searchParams.get('email');

  const [countdown, setCountdown] = useState(0);
  const [intervalId, setIntervalId] = useState(null);

  useEffect(() => {
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [intervalId]);

  const startCountdown = (duration = 60) => {
    setCountdown(duration);

    const id = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(id);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    setIntervalId(id);
  };

  const VerifySchema = Yup.object().shape({
    code: Yup.string()
      .min(6, 'Code must be at least 6 digits')
      .required('Code is required'),
    email: Yup.string()
      .required('Email is required')
      .email('Email must be a valid email address'),
  });

  const defaultValues = {
    code: '',
    email: email || '',
  };

  const methods = useForm({
    mode: 'onChange',
    resolver: yupResolver(VerifySchema),
    defaultValues,
  });

  const {
    watch,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const values = watch();

  const onSubmit = handleSubmit(async (data) => {
    try {
      await validatePin({ email: data.email, pin: data.code }).unwrap();
      toast.success('PIN verified successfully!');
      router.push(`${paths.auth.jwt.newPassword}?email=${encodeURIComponent(data.email)}`);
    } catch (error) {
      toast.error(error?.data?.message || 'Failed to validate PIN');
    }
  });

  const handleResendCode = useCallback(async () => {
    try {
      await resendOtp({ email: values.email }).unwrap();
      toast.success('OTP resent successfully!');
      startCountdown(60);
    } catch (error) {
      toast.error(error?.data?.message || 'Failed to resend OTP');
    }
  }, [resendOtp, values.email]);

  const renderForm = (
    <Stack spacing={3} alignItems="center">
      <RHFTextField
        name="email"
        label="Email"
        placeholder="example@gmail.com"
        InputLabelProps={{ shrink: true }}
        InputProps={{
          readOnly: true,
          sx: {
            pointerEvents: 'none',
            '&:hover fieldset': { borderColor: 'transparent !important' },
            '&.Mui-focused fieldset': { borderColor: 'transparent !important' },
            backgroundColor: 'background.paper',
          },
        }}
      />

      <RHFCode name="code" />

      <LoadingButton
        fullWidth
        size="large"
        type="submit"
        color="primary"
        variant="contained"
        loading={isSubmitting}
      >
        Verify
      </LoadingButton>

      <Typography variant="body2">
        Don’t have a code?{' '}
        <Link
          variant="subtitle2"
          onClick={countdown === 0 ? handleResendCode : undefined}
          sx={{
            cursor: countdown === 0 ? 'pointer' : 'default',
            color: countdown === 0 ? 'primary.main' : 'text.disabled',
            pointerEvents: countdown === 0 ? 'auto' : 'none',
          }}
        >
          {countdown > 0 ? `Resend code (${countdown}s)` : 'Resend code'}
        </Link>
      </Typography>

      <Link
        component={RouterLink}
        href={paths.auth.jwt.login}
        color="inherit"
        variant="subtitle2"
        sx={{ alignItems: 'center', display: 'inline-flex' }}
      >
        <Iconify icon="eva:arrow-ios-back-fill" width={16} />
        Return to sign in
      </Link>
    </Stack>
  );

  const renderHead = (
    <>
      <EmailInboxIcon sx={{ height: { md: 96, xs: 50 } }} />
      <Stack spacing={1} sx={{ my: 5 }}>
        <Typography variant="h3">Please check your email!</Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          We’ve sent a 6-digit code to {email || 'your email'}. Enter it below to verify.
        </Typography>
      </Stack>
    </>
  );

  return (
    <FormProvider methods={methods} onSubmit={onSubmit}>
      {renderHead}
      {renderForm}
    </FormProvider>
  );
}

